import { useCallback, useEffect, useState } from 'react';
import { getRunnables } from '../services/SandboxService';
import { readProjectSettings, updateProjectSettings } from '../utils/defaultApp';
import {
  RUNNABLES_KEY, choicesOf, withChoice, withEntry,
} from '../utils/runnablesModel';

/**
 * The Dockerfiles and compose files found in a project's services and the project's choices for them
 * (utils/runnablesModel.js): `{ scan, docker, dockerOff, choices, choose, reload, loaded }`. `scan` is vfs-service's answer
 * (`{ docker, services }`, null on an older one), `choose({ path, kind, state })` saves a choice in `.flow/project.json`
 * (state null removes it) and resolves once written; `setEntry(path, { service, port })` saves a pinned compose
 * file's preview entry point on its pin entry. Read again when `refreshKey` changes.
 */
export default function useRunnables(projectId, { refreshKey = 0 } = {}) {
  const [scan, setScan] = useState(null);
  const [choices, setChoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (!projectId) { return undefined; }
    let live = true;
    Promise.all([getRunnables(projectId).catch(() => null), readProjectSettings(projectId)])
      .then(([found, settings]) => {
        if (!live) { return; }
        setScan(found);
        setChoices(choicesOf(settings));
        setLoaded(true);
      });
    return () => { live = false; };
  }, [projectId, refreshKey, bump]);

  const choose = useCallback(async(choice) => {
    const next = await updateProjectSettings(projectId, (settings) => {
      const list = withChoice(choicesOf(settings), choice);
      if (list.length) { settings[RUNNABLES_KEY] = list; } else { delete settings[RUNNABLES_KEY]; }
      return settings;
    });
    setChoices(choicesOf(next));
  }, [projectId]);
  // A pinned compose file's preview entry point (`{ service, port }`, null removes it), saved on its pin entry.
  const setEntry = useCallback(async(path, entry) => {
    const next = await updateProjectSettings(projectId, (settings) => {
      settings[RUNNABLES_KEY] = withEntry(choicesOf(settings), path, entry);
      return settings;
    });
    setChoices(choicesOf(next));
  }, [projectId]);
  const reload = useCallback(() => setBump((n) => n + 1), []);

  return { scan, docker: scan?.docker ?? null, dockerOff: scan?.dockerOff ?? null, choices, choose, setEntry, reload, loaded };
}
